// Ultimate Card Game (UCG) - WebSocket Match Client
// Connects to the authoritative Node.js WebSocket Game Server.

import { BALANCE_HASH } from '../data/cards.js';
import { BALANCE } from '../data/balance.js';
import { getAuth, ensureIdentity } from './playerIdentity.js';

export class WSMatchClient {
  // How long to keep retrying a dropped mid-match connection before giving
  // up and reporting it as a real disconnect - matches MatchRoom's 15s
  // forfeit grace period (server/matchRoom.js) so a reconnect that would
  // still have succeeded server-side never gets abandoned client-side
  // first. See docs/SCALING_AUDIT_KRITERIENKATALOG.md K-2.3.
  //
  // Covers a brief connection drop while this tab stays open (WiFi/mobile
  // handoff, phone call, background switch) - NOT a full page reload, which
  // loses the entire client-side Game instance and would need a separate
  // "rebuild game state from a fresh snapshot" flow this does not implement.
  static RECONNECT_WINDOW_MS = 30000;
  static RECONNECT_RETRY_MS = 1500;
  // RTT probe cadence for the in-game net overlay (K-3.3,
  // js/engine/performance.js) - purely observational (server never uses
  // this for keep-alive; the 30 Hz WORLD_STATE stream already keeps the
  // connection far from idle), so a slow cadence is fine.
  // Snapshot wire protocol. 2 = unit packets omit idle-default fields and this
  // client restores them (js/net/snapshotCompaction.js). A client that does not
  // announce it (cached older PWA build) is sent fully expanded packets by the
  // server, so a server deploy never breaks stale clients.
  static PROTOCOL_VERSION = 2;
  static PING_INTERVAL_MS = 3000;
  // The server answers every PING with a PONG (and streams 30 Hz snapshots), so
  // two silent ping intervals mean the socket is half-open: iOS background
  // return or a Wi-Fi -> LTE handover leaves it looking open to the browser
  // for a long time. A silent socket is treated like a closed one.
  static STALE_SOCKET_MS = 7000;

  constructor({
    url = null,
    onState,
    onMatchStart,
    onMatchEnd,
    onQueueWaiting,
    onQueueStatus,
    onCodeCreated,
    onError,
    onAuthInvalid,
    onDisconnect,
    onReconnect,
    onCmdRejected,
    onConnectionLost,
    onReconnecting,
    onReconnected,
    onMatchPaused,
    onOpponentDisconnected,
    onOpponentReconnected,
    onMatchResync,
    onMatchResuming,
    onMatchResumed
  } = {}) {
    this.ws = null;
    this.serverUrl = url || this.detectServerUrl();
    this.onState = onState;
    this.onMatchStart = onMatchStart;
    this.onMatchEnd = onMatchEnd;
    this.onQueueWaiting = onQueueWaiting;
    this.onQueueStatus = onQueueStatus;
    this.onCodeCreated = onCodeCreated;
    this.onError = onError;
    this.onAuthInvalid = onAuthInvalid;
    this.onDisconnect = onDisconnect;
    this.onReconnect = onReconnect;
    this.onConnectionLost = onConnectionLost;
    this.onReconnecting = onReconnecting;
    this.onReconnected = onReconnected;
    this.onMatchPaused = onMatchPaused;
    this.onOpponentDisconnected = onOpponentDisconnected;
    this.onOpponentReconnected = onOpponentReconnected;
    this.onMatchResync = onMatchResync;
    this.onMatchResuming = onMatchResuming;
    this.onMatchResumed = onMatchResumed;
    // A PLAY/DRAW this client sent was rejected server-side (insufficient
    // plasma most commonly - see server/matchRoom.js's handleMessage()).
    // {cmdType: 'PLAY'|'DRAW', slot} - see js/main.js's wiring.
    this.onCmdRejected = onCmdRejected;

    this.roomId = null;
    this.myTeam = null;
    this.opponentName = null;
    this.opponentRace = null;
    this.isConnected = false;
    this.isMatchActive = false;
    this._outbox = [];
    // { roomId, sessionToken } once MATCH_START arrives, cleared on
    // MATCH_END/leave() - the credential _attemptReconnect() presents to
    // reclaim this exact player's slot after a dropped connection.
    this._reconnectInfo = null;

    // Latest round-trip time, read by StateStreamManager.getNetStats() -
    // see js/net/stateStream.js and js/engine/performance.js.
    this.lastPingMs = null;
    this._pingTimer = null;
    this._lastServerMsgAt = null;

    // Mobile background/foreground lifecycle bindings:
    // When incoming calls, app-switches, or device sleep freeze the socket,
    // handleAppResume() immediately validates or reclaims the match connection
    // upon returning to the foreground without waiting for silent TCP timeouts.
    this._onVisibilityBound = () => this._handleVisibilityChange();
    this._onOnlineBound = () => this.handleAppResume();
    this._onFocusBound = () => this.handleAppResume();
    this._onPageShowBound = () => this.handleAppResume();
    this._attachLifecycleListeners();
  }

  _attachLifecycleListeners() {
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', this._onVisibilityBound);
    }
    if (typeof window !== 'undefined') {
      window.addEventListener('online', this._onOnlineBound);
      window.addEventListener('focus', this._onFocusBound);
      window.addEventListener('pageshow', this._onPageShowBound);
    }
  }

  _detachLifecycleListeners() {
    if (typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', this._onVisibilityBound);
    }
    if (typeof window !== 'undefined') {
      window.removeEventListener('online', this._onOnlineBound);
      window.removeEventListener('focus', this._onFocusBound);
      window.removeEventListener('pageshow', this._onPageShowBound);
    }
  }

  _handleVisibilityChange() {
    if (typeof document === 'undefined') return;
    if (document.visibilityState === 'visible') {
      this.handleAppResume();
    } else if (document.visibilityState === 'hidden') {
      // Sent immediately as the tab is suspended into background
      if (this.isMatchActive && this.ws && this.ws.readyState === 1) {
        try { this.ws.send(JSON.stringify({ action: 'PING', t: Date.now() })); } catch {}
      }
    }
  }

  handleAppResume() {
    if (!this.isMatchActive || !this._reconnectInfo) return;
    if (this._reconnecting) return;

    const now = Date.now();
    const silentDuration = now - (this._lastServerMsgAt || 0);
    const isSocketDeadOrStale = !this.ws ||
      this.ws.readyState !== 1 ||
      silentDuration > WSMatchClient.STALE_SOCKET_MS;

    if (isSocketDeadOrStale) {
      const dead = this.ws;
      if (dead) {
        dead.onclose = null;
        dead.onmessage = null;
        dead.onerror = null;
        try { dead.close(); } catch {}
      }
      this.ws = null;
      this.isConnected = false;
      this._attemptReconnect();
    } else {
      // Socket is still open: immediately verify responsiveness with a fast PING probe
      this.send({ action: 'PING', t: now });
    }
  }

  _startPingLoop() {
    this._stopPingLoop();
    this._lastServerMsgAt = Date.now();
    this._pingTimer = setInterval(() => {
      if (this.isMatchActive && this._reconnectInfo && !this._reconnecting &&
          Date.now() - this._lastServerMsgAt > WSMatchClient.STALE_SOCKET_MS) {
        // Detach the dead socket first so its (possibly much later) close
        // event can't start a second reconnect loop, then reclaim the slot.
        const dead = this.ws;
        if (dead) {
          dead.onclose = null;
          dead.onmessage = null;
          dead.onerror = null;
          try { dead.close(); } catch {}
        }
        this.isConnected = false;
        this._attemptReconnect();
        return;
      }
      this.send({ action: 'PING', t: Date.now() });
    }, WSMatchClient.PING_INTERVAL_MS);
  }

  _stopPingLoop() {
    clearInterval(this._pingTimer);
    this._pingTimer = null;
  }

  detectServerUrl() {
    if (typeof window !== 'undefined' && window.location) {
      const host = (window.location.hostname || '').toLowerCase();
      const isLocal = host === 'localhost' || host === '127.0.0.1' || host === '[::1]';
      if (isLocal) {
        return `ws://${window.location.hostname}:${window.location.port || 3000}`;
      }
      if (host.includes('staging.voidconflict.com')) {
        return 'wss://staging-game.voidconflict.com';
      }
      if (host.includes('voidconflict.com')) {
        return 'wss://game.voidconflict.com';
      }
      return 'wss://game.zernecke.de';
    }
    return 'ws://localhost:3000';
  }

  connect() {
    if (this.ws && (this.ws.readyState === 0 || this.ws.readyState === 1)) {
      return Promise.resolve();
    }

    return new Promise((resolve, reject) => {
      try {
        this.ws = new WebSocket(this.serverUrl);
      } catch (err) {
        return reject(err);
      }

      this.ws.onopen = () => {
        this.isConnected = true;
        this._lastServerMsgAt = Date.now();
        while (this._outbox.length > 0) {
          const msg = this._outbox.shift();
          try { this.ws.send(JSON.stringify(msg)); } catch {}
        }
        resolve();
      };

      this.ws.onmessage = (event) => {
        this._lastServerMsgAt = Date.now();
        let data;
        try {
          data = JSON.parse(event.data);
        } catch {
          return;
        }
        this._handleServerMessage(data);
      };

      this.ws.onerror = (err) => {
        if (this.onError) this.onError(err);
      };

      this.ws.onclose = () => {
        this.isConnected = false;
        // A drop mid-match tries to reclaim the same player slot instead
        // of immediately surfacing as a disconnect - see _attemptReconnect().
        if (this.isMatchActive && this._reconnectInfo) {
          this._attemptReconnect();
        } else if (this.onDisconnect) {
          this.onDisconnect();
        }
      };
    });
  }

  // Retries a fresh WebSocket connection for up to RECONNECT_WINDOW_MS,
  // presenting the credential from MATCH_START on each attempt
  // (MatchMaker.reconnect(), server/matchMaker.js). The server answers
  // with an immediate WORLD_STATE snapshot on success (MatchRoom.
  // handleReconnect()) or an ERROR on a stale/invalid token - either one
  // ends the retry loop; a closed/refused connection retries instead.
  _attemptReconnect() {
    if (this._reconnecting) return;
    this._reconnecting = true;
    const deadline = Date.now() + WSMatchClient.RECONNECT_WINDOW_MS;
    const { roomId, sessionToken } = this._reconnectInfo;
    let attempt = 0;

    const remainingSec = Math.ceil(WSMatchClient.RECONNECT_WINDOW_MS / 1000);
    if (this.onConnectionLost) {
      this.onConnectionLost({ remainingSec, deadline });
    }

    const tryOnce = () => {
      const now = Date.now();
      const currentRemaining = Math.max(0, Math.ceil((deadline - now) / 1000));

      if (!this._reconnectInfo || now >= deadline) {
        this._reconnecting = false;
        this._reconnectInfo = null;
        this.isMatchActive = false;
        this._stopPingLoop();
        if (this.onDisconnect) this.onDisconnect();
        return;
      }

      attempt++;
      if (this.onReconnecting) {
        this.onReconnecting({ attempt, remainingSec: currentRemaining, deadline });
      }

      let ws;
      try {
        ws = new WebSocket(this.serverUrl);
      } catch {
        setTimeout(tryOnce, WSMatchClient.RECONNECT_RETRY_MS);
        return;
      }

      let settled = false;
      ws.onopen = () => {
        try {
          ws.send(JSON.stringify({ action: 'RECONNECT', roomId, sessionToken }));
        } catch {}
      };
      ws.onmessage = (event) => {
        let data;
        try {
          data = JSON.parse(event.data);
        } catch {
          return;
        }
        if (data?.type === 'ERROR') {
          // Stale/invalid token (e.g. the match already ended while this
          // tab was offline) - no point retrying with the same credential.
          settled = true;
          this._reconnecting = false;
          this._reconnectInfo = null;
          this.isMatchActive = false;
          this._stopPingLoop();
          try { ws.close(); } catch {}
          if (this.onDisconnect) this.onDisconnect();
          return;
        }
        // Any other message (in practice always the immediate snapshot)
        // confirms the reconnect succeeded - adopt this socket as the live
        // connection and resume normal message handling on it.
        settled = true;
        this._reconnecting = false;
        this._lastServerMsgAt = Date.now();
        this._adoptSocket(ws);
        this._handleServerMessage(data);
        if (this.onReconnected) this.onReconnected();
        if (this.onReconnect) this.onReconnect();
      };
      ws.onerror = () => {};
      ws.onclose = () => {
        if (!settled) setTimeout(tryOnce, WSMatchClient.RECONNECT_RETRY_MS);
      };
    };

    tryOnce();
  }

  // Wires a successfully-reconnected socket up exactly like connect()'s
  // own onopen/onmessage/onclose would, including another reconnect
  // attempt if THIS socket later drops again too.
  _adoptSocket(ws) {
    this.ws = ws;
    this.isConnected = true;
    ws.onmessage = (event) => {
      let data;
      try {
        data = JSON.parse(event.data);
      } catch {
        return;
      }
      this._handleServerMessage(data);
    };
    ws.onerror = (err) => {
      if (this.onError) this.onError(err);
    };
    ws.onclose = () => {
      this.isConnected = false;
      if (this.isMatchActive && this._reconnectInfo) {
        this._attemptReconnect();
      } else if (this.onDisconnect) {
        this.onDisconnect();
      }
    };
  }

  _handleServerMessage(data) {
    if (!data) return;
    this._lastServerMsgAt = Date.now();

    if (data.type === 'WORLD_STATE') {
      if (this.onState) this.onState(data);
      return;
    }

    if (data.type === 'PONG') {
      // t is this client's own Date.now() from when it sent PING - no
      // server clock sync needed, the round trip is purely a local delta.
      if (typeof data.t === 'number') this.lastPingMs = Date.now() - data.t;
      return;
    }

    if (data.type === 'MATCH_START') {
      this.roomId = data.roomId;
      this.myTeam = data.team;
      this.opponentName = data.opponentName;
      this.opponentRace = data.opponentRace;
      this.isMatchActive = true;
      this._reconnectInfo = { roomId: data.roomId, sessionToken: data.sessionToken };
      this._startPingLoop();
      if (this.onMatchStart) this.onMatchStart(data);
      return;
    }

    if (data.type === 'MATCH_END') {
      this.isMatchActive = false;
      this._reconnectInfo = null;
      this._stopPingLoop();
      if (this.onMatchEnd) this.onMatchEnd(data);
      return;
    }

    if (data.type === 'MATCH_PAUSED') {
      if (this.onMatchPaused) this.onMatchPaused(data);
      return;
    }

    if (data.type === 'OPPONENT_DISCONNECTED') {
      if (this.onOpponentDisconnected) this.onOpponentDisconnected(data);
      return;
    }

    if (data.type === 'OPPONENT_RECONNECTED') {
      if (this.onOpponentReconnected) this.onOpponentReconnected(data);
      return;
    }

    if (data.type === 'MATCH_RESYNC') {
      if (this.onMatchResync) this.onMatchResync(data);
      return;
    }

    if (data.type === 'MATCH_RESUMING') {
      if (this.onMatchResuming) this.onMatchResuming(data);
      return;
    }

    if (data.type === 'MATCH_RESUMED') {
      if (this.onMatchResumed) this.onMatchResumed(data);
      return;
    }

    if (data.type === 'QUEUE_WAITING') {
      if (this.onQueueWaiting) this.onQueueWaiting(data.position, data);
      return;
    }

    if (data.type === 'QUEUE_STATUS') {
      if (this.onQueueStatus) this.onQueueStatus(data);
      return;
    }

    if (data.type === 'CODE_CREATED') {
      if (this.onCodeCreated) this.onCodeCreated(data.code);
      return;
    }

    if (data.type === 'ERROR') {
      if (this.onError) this.onError(data.message);
      return;
    }

    if (data.type === 'AUTH_INVALID') {
      if (this.onAuthInvalid) this.onAuthInvalid(data);
      else if (this.onError) this.onError(data.message);
      return;
    }

    if (data.type === 'CMD_REJECTED') {
      if (this.onCmdRejected) this.onCmdRejected(data);
      return;
    }

    if (data.type === 'TAUNT') {
      if (this.onTaunt) this.onTaunt(data);
      return;
    }
  }

  send(obj) {
    if (this.ws && this.ws.readyState === 1) {
      try {
        this.ws.send(JSON.stringify(obj));
      } catch (err) {
        this._outbox.push(obj);
      }
    } else {
      this._outbox.push(obj);
    }
  }

  async joinQueue({ playerName, deck, race, mode, level, avatarId, avatarBorder, allowGuest = false, flatDraw = BALANCE.flatDrawRates }) {
    let auth = getAuth();
    if (!auth || !auth.accountId) {
      try {
        auth = await ensureIdentity();
      } catch {}
    }
    this.send({
      action: 'QUEUE_JOIN',
      playerName,
      deck,
      race,
      mode,
      level,
      avatarId,
      avatarBorder,
      flatDraw: !!flatDraw,
      balanceHash: BALANCE_HASH,
      protocol: WSMatchClient.PROTOCOL_VERSION,
      auth: auth || getAuth(),
      allowGuest: !!allowGuest
    });
  }

  leaveQueue() {
    this.send({ action: 'QUEUE_LEAVE' });
  }

  async createCode({ code, playerName, deck, race, mode, level, avatarId, allowGuest = false, flatDraw = BALANCE.flatDrawRates }) {
    let auth = getAuth();
    if (!auth || !auth.accountId) {
      try {
        auth = await ensureIdentity();
      } catch {}
    }
    this.send({
      action: 'CREATE_CODE',
      code,
      playerName,
      deck,
      race,
      mode,
      level,
      avatarId,
      flatDraw: !!flatDraw,
      balanceHash: BALANCE_HASH,
      protocol: WSMatchClient.PROTOCOL_VERSION,
      auth: auth || getAuth(),
      allowGuest: !!allowGuest
    });
  }

  async joinCode({ code, playerName, deck, race, level, avatarId, allowGuest = false, flatDraw = BALANCE.flatDrawRates }) {
    let auth = getAuth();
    if (!auth || !auth.accountId) {
      try {
        auth = await ensureIdentity();
      } catch {}
    }
    this.send({
      action: 'JOIN_CODE',
      code,
      playerName,
      deck,
      race,
      level,
      avatarId,
      flatDraw: !!flatDraw,
      balanceHash: BALANCE_HASH,
      protocol: WSMatchClient.PROTOCOL_VERSION,
      auth: auth || getAuth(),
      allowGuest: !!allowGuest
    });
  }

  cancelCode(code) {
    this.send({ action: 'CANCEL_CODE', code });
  }

  sendClientCommand(cmd) {
    this.send({
      type: 'CLIENT_CMD',
      cmd
    });
  }

  sendSurrender() {
    this.send({ type: 'SURRENDER' });
  }

  sendTaunt(text) {
    this.send({ type: 'TAUNT', text });
  }

  leave() {
    this.isMatchActive = false;
    this._reconnectInfo = null;
    this._stopPingLoop();
    // Anything still queued belongs to the session being closed - it must
    // not be replayed (e.g. a stale SURRENDER) on the next connect().
    this._outbox.length = 0;
    if (this.ws) {
      try {
        this.ws.close();
      } catch {}
      this.ws = null;
    }
  }

  destroy() {
    this.leave();
    this._detachLifecycleListeners();
  }
}
